import { useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { useToast } from '@up2d8/ui';
import { isNativeApp } from '../../shared/native';
import { isOlderThan } from '../../shared/native-links';
import { installNativeShell } from '../../shared/native-shell';

/**
 * De native laag van de app, voor elk scherm, ook vóór het aanmelden (MOB-2). Hangt aan de
 * root-route: een link uit een uitnodigingsmail (`/app/join/...`) opent de app voordat iemand een
 * account heeft.
 *
 * Toont alleen iets als de app te oud is. De schermen komen van de server, dus een nieuwe functie
 * vraagt geen nieuwe app - behalve als de server iets nodig heeft wat de schil nog niet kan (een
 * nieuwe plugin). Dan zet `public/mobile.json` de oudste versie die nog werkt.
 */
export function NativeShell() {
  const router = useRouter();
  const toast = useToast();
  const { t } = useTranslation();
  const [updateUrl, setUpdateUrl] = useState<string | null>(null);

  useEffect(
    () =>
      installNativeShell({
        navigate: (path) => router.history.push(path),
        goBack: () => router.history.back(),
        isHome: () => router.state.location.pathname === '/app',
        onExitHint: () => toast.show({ tone: 'info', title: t('native.exitHint') }),
      }),
    // `toast` en `t` veranderen niet zolang de app open is: de listeners hangen er één keer.
    [router],
  );

  useEffect(() => {
    if (!isNativeApp()) return;
    void (async () => {
      try {
        const [info, response] = await Promise.all([App.getInfo(), fetch('/mobile.json')]);
        const config = (await response.json()) as {
          minVersion?: string;
          updateUrl?: { android?: string; ios?: string };
        };
        if (config.minVersion && isOlderThan(info.version, config.minVersion)) {
          const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
          setUpdateUrl(config.updateUrl?.[platform] ?? '');
        }
      } catch {
        // Geen netwerk of geen bestand: dan geen melding. Een vals alarm is erger dan geen.
      }
    })();
  }, []);

  if (updateUrl === null) return null;

  return (
    <div
      role="alert"
      className="app-safe-top fixed inset-x-0 top-0 z-50 flex items-center gap-inline bg-brand-dark-blue px-4 text-content text-text-on-dark"
    >
      <span className="min-w-0 flex-1">{t('native.updateNeeded')}</span>
      {updateUrl ? (
        <button
          type="button"
          data-touch-target
          className="shrink-0 rounded-md bg-surface px-3 py-2 font-heading font-semibold text-brand-dark-blue"
          onClick={() => void Browser.open({ url: updateUrl })}
        >
          {t('native.update')}
        </button>
      ) : null}
    </div>
  );
}
